<script lang="ts" setup>
import type { AsyncDataRequestStatus } from '#app'

import ProductSort from '~/components/dropdown-menus/ProductSort.vue'
import SearchInput from '~/components/form-elements/SearchInput.vue'
import CatalogMenu from '~/components/menus/CatalogMenu.vue'
import HorizontalMenu from '~/components/menus/HorizontalMenu.vue'
import ConsultationsSection from '~/components/sections/ConsultationsSection.vue'
import MindboxRecommendationsSection from '~/components/sections/MindboxRecommendationsSection.vue'
import { useWindowHelpers } from '~/composables/window-helpers'
import menuItems from '~/constants/menu-items'
import { CategoryType } from '~/enums/category-type'
import { MindboxOperation } from '~/enums/mindbox-operation'
import PageEnum from '~/enums/page'
import ProductSortEnum from '~/enums/product-sort'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import MindboxApiService from '~/services/mindbox-api-service'
import type { BrandData } from '~/types/brands'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'
import type { CategoryData } from '~/types/categories'
import type { Page } from '~/types/json-api'
import type { ProductData } from '~/types/products'

const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: PageEnum.Main }]

const { $apiHelper } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const { scrollTo, scrollToSavedPosition } = useWindowHelpers()

const isFirstDataLoading = ref(true)
const isProductsDataReloading = ref(false)

// Filter.
const filter = ref({ brandId: null, isOnPreOrder: null, isOnSale: null, query: null, tagId: null })

// Sort.
const sort = ref(ProductSortEnum.MostPopular)

// Pagination.
const page = ref<Page>({ number: 1, size: 30 })

const title = computed(() => filter.value.query ? 'Результаты поиска' : 'Каталог')

const { data, error, status } = await useLazyAsyncData(
  'products-index',
  async () => {
    parseQuery()

    const brandParams = { 'page[size]': 1000 }
    const { ActiveIngredient, BodySystem } = CategoryType
    const categoryParams = { 'page[size]': 1000, 'filter[typeIds]': [ActiveIngredient, BodySystem] }

    const [brandsResponse, categoriesResponse, productsResponse] = await Promise.all([
      $apiHelper.brands.getBrands(brandParams),
      $apiHelper.categories.getCategories(categoryParams),
      $apiHelper.products.getProducts(getProductsParams()),
    ])

    return { brandsResponse, categoriesResponse, productsResponse }
  },
  {
    transform: (x) => ({
      brandsResponse: JsonApiHelper.denormalizeResponse(x?.brandsResponse),
      categoriesResponse: JsonApiHelper.denormalizeResponse(x?.categoriesResponse),
      productsResponse: JsonApiHelper.denormalizeResponse(x?.productsResponse)
    })
  }
)

const productsData = computed<ProductData[]>(() => data.value?.productsResponse?.data ?? [])
const categoriesData = computed<CategoryData[]>(() => data.value?.categoriesResponse?.data ?? [])
const brandsData = computed<BrandData[]>(() => data.value?.brandsResponse?.data ?? [])
const totalProducts = computed(() => data.value?.productsResponse?.meta.totalProducts)
const totalPages = computed(
  () => data.value?.productsResponse
    ? helpers.getTotalPages(data.value?.productsResponse.meta.totalProducts, page.value.size)
    : null
)

onBeforeMount(parseQuery)

onMounted(() => {
  isFirstDataLoading.value = false
})

useHead({
  title: 'Каталог БАД и витаминов интернет-магазина iTAB',
  meta: [{
    name: 'description',
    content: 'В нашем каталоге вы найдёте самые разнообразные пищевые добавки и другие товары для поддержания тонуса организма, здоровья и иммунитета.'
  }]
})

watch(
  [page, sort],
  async (): Promise<void> => {
    if (!isFirstDataLoading.value) {
      scrollTo('scroll-anchor', 60)

      await updateQuery()

      await reloadProductsData()
    }
  },
  { deep: true }
)
watch(
  productsData,
  (x: ProductData[]): void => {
    if (filter.value.query) {
      MindboxApiService.setSearch(x.slice(0, 3))
    }
  },
  { deep: true }
)
watch(
  sort,
  (): void => {
    if (isFirstDataLoading.value) {
      return
    }

    changePage(1)
  }
)
watch(
  status,
  (x: AsyncDataRequestStatus): void => {
    if (x === 'success') {
      nextTick(scrollToSavedPosition)
    }
  }
)

function changePage (pageNumber: number): void {
  page.value.number = pageNumber
}

function getProductsParams (): any {
  const { brandId, isOnPreOrder, isOnSale, query, tagId } = filter.value
  const { number, size } = page.value

  return helpers.getParams([
    { name: 'filter[brandId]', value: brandId, condition: brandId !== null },
    { name: 'filter[isOnPreOrder]', value: isOnPreOrder, condition: isOnPreOrder !== null },
    { name: 'filter[isOnSale]', value: isOnSale, condition: isOnSale !== null },
    { name: 'filter[query]', value: query, condition: query },
    { name: 'filter[tagId]', value: tagId, condition: tagId !== null },
    { name: 'page[number]', value: number, condition: true },
    { name: 'page[size]', value: size, condition: true },
    { name: 'sort', value: sort.value, condition: sort.value }
  ])
}

async function goToProductsPage (): Promise<void> {
  const { query } = filter.value

  if (query) {
    await navigateTo(`${PageEnum.Search}?query=${encodeURIComponent(query)}`)
  }
}

function parseQuery (): void {
  const { brandId, isOnPreOrder, isOnSale, page: _page, query, sort: _sort, tagId } = route.query
  const { MostPopular } = ProductSortEnum

  filter.value.brandId = brandId !== undefined ? brandId : null
  filter.value.isOnPreOrder = isOnPreOrder === 'yes' ? isOnPreOrder : null
  filter.value.isOnSale = isOnSale === 'yes' ? isOnSale : null
  page.value.number = _page !== undefined && /^\+?[1-9]\d*$/.test(String(_page)) && _page !== '1' ? Number(_page) : 1
  filter.value.query = query !== undefined ? query : null
  sort.value = _sort !== undefined && Object.values(ProductSortEnum).includes(String(_sort)) && _sort !== MostPopular
    ? _sort
    : MostPopular
  filter.value.tagId = tagId !== undefined ? tagId : null
}

async function reloadProductsData (): Promise<void> {
  isProductsDataReloading.value = true

  const response = await $apiHelper.products.getProducts(getProductsParams())
  const { data: productsData } = JsonApiHelper.denormalizeResponse(response)

  if (data.value?.productsResponse) {
    data.value.productsResponse.data = productsData
  }

  isProductsDataReloading.value = false
}

async function updateQuery (): Promise<void> {
  const { brandId, isOnPreOrder, isOnSale, query, tagId } = filter.value
  const { number } = page.value

  const params = [
    { name: 'brandId', value: brandId, condition: brandId !== null },
    { name: 'isOnPreOrder', value: isOnPreOrder, condition: isOnPreOrder !== null },
    { name: 'isOnSale', value: isOnSale, condition: isOnSale !== null },
    { name: 'page', value: number, condition: number !== 1 },
    { name: 'query', value: query, condition: query !== null },
    { name: 'sort', value: sort.value, condition: sort.value !== null && sort.value !== ProductSortEnum.MostPopular },
    { name: 'tagId', value: tagId, condition: tagId !== null }
  ]
  const newQuery: any = {}

  for (const { condition, name, value } of params) {
    if (condition) {
      newQuery[name] = value
    }
  }

  // To avoid an error: "Avoided redundant navigation to current location".
  try {
    await router.replace({ query: newQuery })
  } catch {}
}
</script>

<template>
  <div>
    <section>
      <div class="container">
        <Breadcrumbs :current="title" :items="breadcrumbsItems" />

        <Heading :badge-text="totalProducts" :title="title" />

        <SearchInput
          v-if="false"
          v-model="filter.query"
          class="mb-6"
          placeholder="Введите название, симптом, систему организма или активное вещество"
          @enter="goToProductsPage"
          @on-button-click="goToProductsPage"
        />
      </div>
    </section>

    <template v-if="status === 'success'">
      <CatalogMenu :brands-data="brandsData" :categories-data="categoriesData" class="mb-4" />

      <section>
        <div class="container">
          <HorizontalMenu :items="menuItems.products" />
        </div>
      </section>
    </template>

    <Preloader v-if="status === 'pending' || isProductsDataReloading" />
    <ProductsWithPagination
      v-else
      v-model:sort="sort"
      :page="page"
      :products-data="productsData"
      :query="filter.query"
      :total-pages="totalPages"
      @change-page="changePage"
    />

    <MindboxRecommendationsSection
      v-if="productsData.length"
      class="mt-10"
      :data="{ recommendation: { limit: 12 } }"
      :operation="MindboxOperation.PopularProducts"
      :path="PageEnum.Search"
      :query-parameters="{ sort: ProductSort.MostPopular }"
      title="Популярное"
    />

    <ConsultationsSection class="mt-10" />
  </div>
</template>
